/**
 * NOT-590 — pricebook category management.
 *
 * ONE tree for the whole catalog. A folder holds services, materials and
 * equipment side by side: "Filters" is one place to look, not three. Splitting
 * the tree by item type made the folder answer a question it has no business
 * answering — the item's own type already says what kind of thing it is.
 *
 * Categories are for BROWSING. They carry no money and are never a reporting
 * dimension — "revenue by category" is answered by business unit, job type or
 * item type, and this page will not grow a fourth answer.
 */
import { useCallback, useMemo, useState, type CSSProperties, type PointerEvent } from 'react';
import { createFileRoute } from '@tanstack/react-router';
import { Folder } from 'lucide-react';
import { CategoryDetailPanel } from '@/components/organisms/pricebook/categories/CategoryDetailPanel';
import { PricebookFolderNavigator } from '@/components/organisms/pricebook/categories/PricebookFolderNavigator';
import {
  useAddItemsToCategory,
  useBulkEditCategoryItems,
  useCategoryItems,
  useCategoryTree,
  type CategoryFamily,
  useDeleteCategory,
  useRemoveItemsFromCategory,
  useReorderCategoryItems,
  useUpdateCategory,
  useUpdateCategoryScope,
} from '@/hooks/pricebook/usePricebookCategories';
import { useAuthPermissions } from '@/stores/auth.store';
import { type PricebookCategoryNode } from '@/types/pricebook';
import { useCategoryDeleteFlow } from './-category-delete-flow';
import { BackToPricebookLink } from './-categories-navigation';

export const Route = createFileRoute('/_dashboard/dashboard/pricebook/categories')({
  component: PricebookCategoriesPage,
});

function findNode(nodes: PricebookCategoryNode[], id: string): PricebookCategoryNode | null {
  for (const node of nodes) {
    if (node.id === id) return node;
    const hit = findNode(node.children, id);
    if (hit) return hit;
  }
  return null;
}

/**
 * How wide the folder tree is. A deep catalog truncates names at ANY fixed
 * width — "Proven Residential HVAC Services" does not fit in 352px — so the
 * operator sets it and the choice is remembered per browser.
 */
const TREE_WIDTH_KEY = 'pricebook-categories-tree-width';
const TREE_MIN_PX = 260;
const TREE_MAX_PX = 640;
const TREE_DEFAULT_PX = 352;

const clampTreeWidth = (px: number) => Math.min(TREE_MAX_PX, Math.max(TREE_MIN_PX, Math.round(px)));

function readTreeWidth(): number {
  try {
    const raw = window.localStorage.getItem(TREE_WIDTH_KEY);
    const parsed = raw === null ? Number.NaN : Number(raw);
    if (Number.isFinite(parsed)) return clampTreeWidth(parsed);
  } catch {
    // Private mode or blocked storage — fall through to the default.
  }
  return TREE_DEFAULT_PX;
}

function PricebookCategoriesPage() {
  const permissions = useAuthPermissions();
  const canManage = permissions.includes('pricebook_categories:manage');
  const canManageScope = permissions.includes('pricebook_categories:manage_scope');

  const [selectedId, setSelectedId] = useState<string | null>(null);
  // Each family is its own tree: a shop's folders for parts are not its
  // folders for work, and "Black" means something different in each.
  const [family, setFamily] = useState<CategoryFamily>('SERVICE');

  // The navigator issues this same query; react-query serves both from one
  // fetch. The page needs the nodes only to resolve which folder the detail
  // pane is showing.
  const treeQuery = useCategoryTree({ manage: canManageScope, itemFamily: family });
  const nodes = useMemo(() => treeQuery.data ?? [], [treeQuery.data]);
  const selected = selectedId ? findNode(nodes, selectedId) : null;

  const updateCategory = useUpdateCategory();
  const updateScope = useUpdateCategoryScope();
  const deleteCategory = useDeleteCategory();
  const deleteFlow = useCategoryDeleteFlow(deleteCategory);
  const bulkEdit = useBulkEditCategoryItems();
  const addItems = useAddItemsToCategory();
  const removeItems = useRemoveItemsFromCategory();
  const reorderItems = useReorderCategoryItems();

  // Keep the selected folder rendered while its delete is pending, but stop
  // observing its item query. The successful delete invalidates category
  // caches before the mutation call-site's onSuccess runs; without this gate,
  // that invalidation refetches /items for the now-deleted id and shows a 404
  // even though the DELETE itself succeeded.
  const itemsQuery = useCategoryItems(selected?.id, {
    manage: canManageScope,
    enabled: !deleteFlow.isDeleting(selected?.id),
  });
  const items = useMemo(() => itemsQuery.data ?? [], [itemsQuery.data]);

  const isSaving = updateCategory.isPending || updateScope.isPending || bulkEdit.isPending;

  // Lazy initial state: reading localStorage on every render would be wasted
  // work, and the first paint must already be the operator's chosen width.
  const [treeWidth, setTreeWidth] = useState(readTreeWidth);

  const commitWidth = useCallback((next: number) => {
    const clamped = clampTreeWidth(next);
    setTreeWidth(clamped);
    try {
      window.localStorage.setItem(TREE_WIDTH_KEY, String(clamped));
    } catch {
      // The width still works for this session; only persistence is lost.
    }
  }, []);

  /**
   * Pointer capture rather than window listeners: the drag keeps tracking when
   * the cursor leaves the 1px divider (which it immediately does), and the
   * browser releases capture for us if the pointer is cancelled.
   */
  const startDrag = (event: PointerEvent<HTMLDivElement>) => {
    event.preventDefault();
    const startX = event.clientX;
    const startWidth = treeWidth;
    const handle = event.currentTarget;
    handle.setPointerCapture(event.pointerId);

    const onMove = (moveEvent: globalThis.PointerEvent) => {
      commitWidth(startWidth + (moveEvent.clientX - startX));
    };
    const onEnd = () => {
      handle.removeEventListener('pointermove', onMove);
      handle.removeEventListener('pointerup', onEnd);
      handle.removeEventListener('pointercancel', onEnd);
    };

    handle.addEventListener('pointermove', onMove);
    handle.addEventListener('pointerup', onEnd);
    handle.addEventListener('pointercancel', onEnd);
  };

  return (
    <div className="flex h-full flex-col gap-4 p-4">
      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <BackToPricebookLink />
          <h1 className="text-[28px] font-medium tracking-[-0.03em]">Categories</h1>
        </div>

        <div className="flex shrink-0 gap-1" role="tablist" aria-label="Category family">
          {(
            [
              { value: 'SERVICE', label: 'Services & bundles' },
              { value: 'MATERIAL', label: 'Materials' },
              { value: 'EQUIPMENT', label: 'Equipment' },
            ] as { value: CategoryFamily; label: string }[]
          ).map((tab) => (
            <button
              key={tab.value}
              type="button"
              role="tab"
              aria-selected={family === tab.value}
              onClick={() => {
                setFamily(tab.value);
                // The previous family's folder is not in this tree.
                setSelectedId(null);
              }}
              className={`flex h-[34px] items-center rounded-full border px-[15px] text-sm font-medium transition-colors ${
                family === tab.value
                  ? 'border-primary bg-primary text-primary-foreground'
                  : 'border-border bg-transparent text-foreground/80 hover:bg-muted'
              }`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      {/* ONE card split into two columns, not two cards with a gap: the divider
          is a border on the tree column, so the pair reads as a single surface.
          The tree is `0 1 352px` with a 260px floor — it gives width back on a
          narrow window instead of forcing the whole card to scroll. */}
      <div className="flex min-h-0 flex-1 flex-col overflow-hidden rounded-[14px] border bg-card shadow-[0_1px_2px_0_rgba(10,11,13,0.04)] lg:flex-row">
        {/* The width rides a custom property so it applies ONLY at lg. Below
            that the card stacks, where a flex-basis would set the HEIGHT. */}
        <aside
          style={{ '--tree-w': `${treeWidth}px` } as CSSProperties}
          className="flex min-h-0 shrink-0 basis-auto flex-col overflow-hidden border-b lg:basis-[var(--tree-w)] lg:border-b-0"
        >
          {/* The same navigator the item form opens in a dialog — create, move
              and rename behave identically in both places. */}
          <PricebookFolderNavigator
            key={family}
            itemFamily={family}
            activeId={selectedId}
            onActivate={(node) => setSelectedId(node.id)}
            readOnly={!canManage}
          />
        </aside>

        {/* The divider IS the drag handle. It stays 1px so the two panes still
            read as one surface; the grab area is widened with a pseudo-element
            instead of real width. Keyboard-operable, because a control you can
            only reach with a mouse is decoration. */}
        <div
          role="separator"
          aria-orientation="vertical"
          aria-label="Resize folder list"
          aria-valuenow={treeWidth}
          aria-valuemin={TREE_MIN_PX}
          aria-valuemax={TREE_MAX_PX}
          tabIndex={0}
          onPointerDown={startDrag}
          onDoubleClick={() => commitWidth(TREE_DEFAULT_PX)}
          onKeyDown={(event) => {
            const step = event.shiftKey ? 48 : 16;
            if (event.key === 'ArrowLeft') {
              event.preventDefault();
              commitWidth(treeWidth - step);
            } else if (event.key === 'ArrowRight') {
              event.preventDefault();
              commitWidth(treeWidth + step);
            } else if (event.key === 'Home') {
              event.preventDefault();
              commitWidth(TREE_MIN_PX);
            } else if (event.key === 'End') {
              event.preventDefault();
              commitWidth(TREE_MAX_PX);
            }
          }}
          className="relative hidden w-px shrink-0 cursor-col-resize bg-border transition-colors before:absolute before:inset-y-0 before:-left-1.5 before:-right-1.5 before:content-[''] hover:bg-primary/40 focus-visible:bg-primary focus-visible:outline-none lg:block"
        />

        <section className="min-h-0 min-w-0 flex-1 overflow-y-auto">
          {selected ? (
            <CategoryDetailPanel
              key={selected.id}
              category={selected}
              items={items}
              isLoadingItems={itemsQuery.isLoading}
              canManageScope={canManageScope}
              readOnly={!canManage}
              isSaving={isSaving}
              // One mutation instance owns this page. Keep every category's
              // delete action disabled until it settles so a second delete
              // cannot replace the per-call callbacks for the first.
              isDeleting={deleteCategory.isPending}
              onAddItems={(lineItemIds) =>
                addItems.mutate({ categoryId: selected.id, lineItemIds })
              }
              onRemoveItem={(lineItemId) =>
                removeItems.mutate({
                  categoryId: selected.id,
                  lineItemIds: [lineItemId],
                })
              }
              onReorderItems={(lineItemIds) =>
                reorderItems.mutate({ categoryId: selected.id, lineItemIds })
              }
              onSave={(dto) => updateCategory.mutate({ id: selected.id, dto })}
              onSaveScope={(dto) => updateScope.mutate({ id: selected.id, dto })}
              onBulkEdit={(dto, includeDescendants) =>
                bulkEdit.mutate({ categoryId: selected.id, dto, includeDescendants })
              }
              onDelete={() => {
                const id = selected.id;
                deleteFlow.remove(id, () => {
                  // Do not clear a newer selection if the operator moved on
                  // while this request was in flight.
                  setSelectedId((current) => (current === id ? null : current));
                });
              }}
            />
          ) : (
            <TopLevelGrid nodes={nodes} onOpen={(node) => setSelectedId(node.id)} />
          )}
        </section>
      </div>
    </div>
  );
}

/**
 * What the detail pane shows before a folder is picked: the family's top-level
 * folders as cards. An empty pane wastes the widest part of the page and tells
 * the operator nothing about the catalog they just opened.
 *
 * The count here is the SUBTREE total, not the direct count the tree row
 * badges — a root folder usually holds nothing itself, so the direct number
 * would read "0 items" for a folder with hundreds under it.
 */
function TopLevelGrid({
  nodes,
  onOpen,
}: {
  nodes: PricebookCategoryNode[];
  onOpen: (node: PricebookCategoryNode) => void;
}) {
  // The navigator already owns the "no folders yet" case, with the button that
  // fixes it. A second empty state here would just be noise beside it.
  if (nodes.length === 0) return null;

  return (
    <div className="px-6 py-5">
      <p className="mb-3 font-mono text-[10px] uppercase tracking-[0.15em] text-muted-foreground">
        Top level
      </p>
      {/* auto-fit, not a fixed card width: the pane narrows as the folder
          splitter is dragged, and fixed cards drop a whole column at a time
          and leave the freed width as dead space on the right. Columns here
          take whatever is left over instead, so the row always fills. */}
      <div className="grid gap-3 [grid-template-columns:repeat(auto-fit,minmax(200px,1fr))]">
        {nodes.map((node) => (
          <button
            key={node.id}
            type="button"
            onClick={() => onOpen(node)}
            className="flex cursor-pointer flex-col gap-2 rounded-[12px] border bg-card p-4 text-left transition-colors hover:bg-muted/40"
          >
            <Folder className="mb-1 size-5 shrink-0 text-muted-foreground" />
            <span className="truncate text-[15px] font-medium">{node.name}</span>
            <span className="text-[11px] uppercase tracking-[0.07em] text-muted-foreground">
              {node.totalItemCount} item{node.totalItemCount === 1 ? '' : 's'}
              {node.children.length > 0
                ? ` · ${node.children.length} subfolder${node.children.length === 1 ? '' : 's'}`
                : ''}
            </span>
          </button>
        ))}
      </div>
    </div>
  );
}
